import { PHARADAY_HARBOR_URL } from "@pharaday/env";
import axios, { type AxiosInstance } from "axios";
import { injectable } from "inversify";

import {
  AccessToken,
  type AccessTokenPayload,
  type AuthenticatedCredentials,
  type AuthenticatorProvider,
  type AuthModeEnum,
  RefreshToken,
  type SelfSignUpConsent,
  type SelfSignUpResult,
  type Token,
} from "../../../core";
import { decodeJwt } from "../../../utils";
import { AuthContainer } from "../../auth.container";
import type { RequestRefreshTokenDto } from "./dto/request-refresh-token.dto";
import type { SelfSignUpDto } from "./dto/self-sign-up.dto";
import type { SignInPrecheckDto } from "./dto/sign-in-precheck.dto";
import type { SignInDto } from "./dto/sign-in.dto";
import type { SSOSSignInUrlDto } from "./dto/sso-sign-in-url.dto";
import type { ValidateTokenDto } from "./dto/validate-token.dto";

@injectable()
export class RestAuthenticatorProvider implements AuthenticatorProvider {
  private readonly axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      baseURL: AuthContainer.get<string>(PHARADAY_HARBOR_URL),
    });
  }

  async signInPrecheck(email: string): Promise<{ authMode: AuthModeEnum }> {
    const { data } = await this.axios.get<SignInPrecheckDto>(`auth/signin/precheck/${encodeURIComponent(email)}`);
    return data;
  }

  async signInWithPassword(
    email: string,
    password: string
  ): Promise<AuthenticatedCredentials | { pendingToken: string }> {
    try {
      const { data } = await this.axios.post<SignInDto>(`auth/signin/with-password`, {
        email,
        password,
      });

      return this.toAuthenticatedCredentials(data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const cause = error?.response?.data.cause as { reason: string; pendingToken?: string };

        if (cause.reason === "AUTH_EMAIL_VERIFICATION_REQUIRED") {
          return { pendingToken: cause.pendingToken! };
        }
      }

      return Promise.reject(error);
    }
  }

  async signInWithMagicLink(email: string, code: string): Promise<AuthenticatedCredentials> {
    const { data } = await this.axios.post<SignInDto>(`auth/signin/with-magic-link`, {
      email,
      code,
    });

    return this.toAuthenticatedCredentials(data);
  }

  async signInWithValidationEmail(pendingToken: string, code: string): Promise<AuthenticatedCredentials> {
    const { data } = await this.axios.post<SignInDto>(`auth/signin/with-validation-email`, {
      pendingToken,
      code,
    });

    return this.toAuthenticatedCredentials(data);
  }

  async signInWithTotp(pendingToken: string, challenge: string, code: string): Promise<AuthenticatedCredentials> {
    const { data } = await this.axios.post<SignInDto>(`auth/signin/with-magic-link`, {
      pendingToken,
      challenge,
      code,
    });

    return this.toAuthenticatedCredentials(data);
  }

  async signInWithSso(code: string): Promise<AuthenticatedCredentials> {
    const { data } = await this.axios.post<SignInDto>(`auth/signin/with-sso`, {
      code,
    });

    return this.toAuthenticatedCredentials(data);
  }

  async completeSelfSignUp(
    token: string,
    code: string,
    name: string,
    consent: SelfSignUpConsent
  ): Promise<AuthenticatedCredentials & SelfSignUpResult> {
    const { data } = await this.axios.post<SelfSignUpDto>(`auth/self-signup`, {
      token,
      code,
      name,
      acceptsTermsAndConditions: consent.acceptsTermsAndConditions,
      acceptsPrivacyPolicy: consent.acceptsPrivacyPolicy,
    });

    return {
      ...this.toAuthenticatedCredentials(data),
      containerId: data.containerId,
      invitationId: data.invitationId,
    };
  }

  async requestSignInSsoUrl(email: string): Promise<{ ssoUrl: string }> {
    const { data } = await this.axios.get<SSOSSignInUrlDto>(`auth/signin/sso-url/${encodeURIComponent(email)}`);
    return data;
  }

  async requestRefreshToken(
    accessToken: AccessToken,
    refreshToken: RefreshToken
  ): Promise<{
    accessToken: AccessToken;
    refreshToken: RefreshToken;
  }> {
    const { data } = await this.axios.post<RequestRefreshTokenDto>(`auth/request/refresh-token`, undefined, {
      headers: {
        Authorization: `Bearer ${accessToken.value}`,
        "X-Refresh-Token": refreshToken.value,
      },
    });

    return {
      accessToken: new AccessToken(data.accessToken),
      refreshToken: new RefreshToken(data.refreshToken),
    };
  }

  async signOut(accessToken: AccessToken, refreshToken: RefreshToken): Promise<{ success: boolean }> {
    await this.axios.post(
      `auth/signout`,
      { refreshToken: refreshToken.value },
      { headers: { Authorization: `Bearer ${accessToken.value}` } }
    );

    return { success: true };
  }

  private toAuthenticatedCredentials(dto: SignInDto): AuthenticatedCredentials {
    return {
      accessToken: new AccessToken(dto.accessToken),
      refreshToken: new RefreshToken(dto.refreshToken),
    };
  }

  async validateToken(token: Token): Promise<{ valid: boolean }> {
    const { data } = await this.axios.get<ValidateTokenDto>(
      `auth/request/token-validation/${token.type}/${token.value}`
    );

    return data;
  }

  extractAccessToken(accessToken: AccessToken): AccessTokenPayload {
    return decodeJwt<AccessTokenPayload>(accessToken.value);
  }

  async requestMagicLink(email: string): Promise<void> {
    await this.axios.post(`auth/request/magic-link`, {
      email,
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.axios.post(`auth/password-reset/request`, {
      email,
    });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.axios.post(`auth/password-reset/${token}`, {
      newPassword: password,
    });
  }
}
